import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Cable,
  Check,
  ChevronsUpDown,
  Key,
  Plus,
  Settings,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTenant } from "@/lib/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { trpc, toastTrpcError } from "@/lib/trpc";

export function WorkspaceSwitcher({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const tenantId = useTenant();
  const utils = trpc.useUtils();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");

  const { data: workspaces, isLoading } = trpc.tenant.listForUser.useQuery();
  const currentWorkspace =
    workspaces?.find((w) => w.isCurrent) ??
    workspaces?.find((w) => w.id === tenantId);

  const switchMutation = trpc.profile.switchWorkspace.useMutation({
    onSuccess: () => {
      toast.success("Switched workspace");
      onNavigate?.();
      window.location.href = "/projects";
    },
    onError: (err) => toastTrpcError(err, "Failed to switch workspace"),
  });

  const createMutation = trpc.tenant.create.useMutation({
    onSuccess: (created) => {
      toast.success(`Workspace "${created.name}" created`);
      setShowCreateDialog(false);
      setNewWorkspaceName("");
      utils.tenant.listForUser.invalidate();
      onNavigate?.();
      window.location.href = "/projects";
    },
    onError: (err) => toastTrpcError(err, "Failed to create workspace"),
  });

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newWorkspaceName.trim()) return;
    createMutation.mutate({ name: newWorkspaceName.trim() });
  };

  const initial = currentWorkspace?.name?.[0]?.toUpperCase() ?? "W";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex w-full items-center gap-2 rounded-[7px] border border-sidebar-border/60 bg-sidebar-accent/20 px-2.5 py-[5px] text-left transition-colors hover:bg-sidebar-accent hover:border-sidebar-border"
            aria-label={`Switch workspace — current: ${currentWorkspace?.name ?? "Workspace"}`}
          >
            <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] bg-sidebar-foreground/10 text-xs font-bold text-sidebar-foreground">
              {initial}
            </span>
            <span className="flex-1 truncate text-xs font-semibold text-sidebar-foreground">
              {isLoading
                ? "Loading..."
                : (currentWorkspace?.name ?? "Workspace")}
            </span>
            <ChevronsUpDown
              className="h-2.5 w-2.5 shrink-0 text-sidebar-muted-foreground"
              aria-hidden="true"
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[var(--radix-dropdown-menu-trigger-width)]"
        >
          <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
            Workspaces
          </DropdownMenuLabel>
          <div className="max-h-48 overflow-y-auto">
            {workspaces?.map((workspace) => (
              <DropdownMenuItem
                key={workspace.id}
                onClick={() => {
                  if (workspace.id === tenantId) return;
                  switchMutation.mutate({ tenantId: workspace.id });
                }}
                disabled={switchMutation.isPending}
              >
                <Check
                  className={cn(
                    "mr-2 h-3.5 w-3.5 shrink-0",
                    workspace.id === tenantId ? "opacity-100" : "opacity-0",
                  )}
                />
                <div className="flex-1 min-w-0">
                  <span className="block truncate font-medium text-sm">
                    {workspace.name}
                  </span>
                  <span className="block text-xs text-muted-foreground capitalize">
                    {workspace.role}
                  </span>
                </div>
              </DropdownMenuItem>
            ))}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              navigate("/settings");
              onNavigate?.();
            }}
          >
            <Settings className="mr-2 h-3.5 w-3.5" />
            Workspace settings
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate("/settings?tab=members");
              onNavigate?.();
            }}
          >
            <Users className="mr-2 h-3.5 w-3.5" />
            Manage members
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate("/settings?tab=access-tokens");
              onNavigate?.();
            }}
          >
            <Key className="mr-2 h-3.5 w-3.5" />
            Access tokens
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate("/settings?tab=usage");
              onNavigate?.();
            }}
          >
            <BarChart3 className="mr-2 h-3.5 w-3.5" />
            Usage
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              navigate("/settings?tab=integrations");
              onNavigate?.();
            }}
          >
            <Cable className="mr-2 h-3.5 w-3.5" />
            Integrations
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => {
              setShowCreateDialog(true);
            }}
          >
            <Plus className="mr-2 h-3.5 w-3.5" />
            Create workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Create New Workspace</DialogTitle>
              <DialogDescription>
                Create a team workspace to collaborate with others across
                projects.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-workspace-name">Workspace name</Label>
                <Input
                  id="new-workspace-name"
                  placeholder="Acme Corp, Studio..."
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  autoFocus
                  required
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!newWorkspaceName.trim() || createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create Workspace"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
